import { BufferAttribute, BufferGeometry } from 'three';
import type { CitizenAsset, CitizenLod } from './citizenAsset';

/**
 * A citizen as the geometry a batch of citizens draws (render/shaders/painted.vert): three
 * vertices a triangle, since a face has one colour and one normal, each with the bones it follows.
 * The channels are as narrow as they can be drawn from, for there are a third of a million
 * vertices in the city's figures: normals in bytes, colours in 16 bits, bones and weights in bytes.
 */
export function citizenGeometry(asset: CitizenAsset, lod: CitizenLod): BufferGeometry {
  const corners = lod.triangles * 3;
  const position = new Float32Array(corners * 3),
    normal = new Int8Array(corners * 3),
    color = new Uint16Array(corners * 3),
    joints = new Uint8Array(corners * 4),
    weights = new Uint8Array(corners * 4);
  const step = asset.positionStepM;
  for (let t = 0; t < lod.triangles; t++) {
    const face = t * 6;
    for (let c = 0; c < 3; c++) {
      const v = lod.faces[face + c]!,
        at = (t * 3 + c) * 3;
      position[at] = lod.places[v * 3]! * step;
      position[at + 1] = lod.places[v * 3 + 1]! * step;
      position[at + 2] = lod.places[v * 3 + 2]! * step;
      color.set(lod.faces.subarray(face + 3, face + 6), at);
      joints.set(lod.joints.subarray(v * 4, v * 4 + 4), (t * 3 + c) * 4);
      weights.set(lod.weights.subarray(v * 4, v * 4 + 4), (t * 3 + c) * 4);
    }
    const a = t * 9;
    const [ux, uy, uz] = [
      position[a + 3]! - position[a]!,
      position[a + 4]! - position[a + 1]!,
      position[a + 5]! - position[a + 2]!,
    ];
    const [vx, vy, vz] = [
      position[a + 6]! - position[a]!,
      position[a + 7]! - position[a + 1]!,
      position[a + 8]! - position[a + 2]!,
    ];
    const [nx, ny, nz] = [uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx];
    const length = Math.hypot(nx, ny, nz) || 1;
    for (let c = 0; c < 3; c++) {
      normal[a + c * 3] = Math.round((nx / length) * 127);
      normal[a + c * 3 + 1] = Math.round((ny / length) * 127);
      normal[a + c * 3 + 2] = Math.round((nz / length) * 127);
    }
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(position, 3));
  geometry.setAttribute('normal', new BufferAttribute(normal, 3, true));
  geometry.setAttribute('color', new BufferAttribute(color, 3, true));
  geometry.setAttribute('aJoints', new BufferAttribute(joints, 4));
  geometry.setAttribute('aWeights', new BufferAttribute(weights, 4, true));
  return geometry;
}
